import React, { memo, startTransition, useEffect, useMemo } from 'react';
import { Object3D, Vector3 } from 'three';

import { CommanderActionTypesEnum } from '@spazious/dollhouse-commander';
import { CanvasLabel, useSelection } from '@spazious/dollhouse-engine';
import { FNodeTypesEnum, FurnitureNode, getNodeFromObject } from '@spazious/dollhouse-fsetup';
import { FShallowNode } from '@spazious/dollhouse-shallow-graph';
import { useStaging, useStore } from '@spazious/dollhouse-store';
import { ToolModeEnum } from '@spazious/dollhouse-types';
import { useEventCallback, useForceUpdate } from '@spazious/react-hooks';

import { useDragPreviewOffset } from '../../hooks/index.js';

/**
 * Wraps a numbering badge so it follows the visual-only drag preview of its
 * furniture, or of the composition owning it, instead of lagging behind until
 * the translate is committed at drop.
 */
const NumberingLabelWrapper: React.FC<React.PropsWithChildren<{ selector: string }>> = ({
  selector,
  children,
}) => {
  const groupRef = useDragPreviewOffset(selector);
  return <group ref={groupRef}>{children}</group>;
};

/**
 * Read a theme CSS custom property and normalize it to an `{ color, opacity }`
 * pair `CanvasLabel` can consume (its WebGL materials take an opaque color plus
 * a separate opacity, so an `rgba()` token must be split). Falls back when the
 * variable is missing (e.g. non-DOM environments).
 */
const readThemeColor = (name: string, fallback: string): { color: string; opacity: number } => {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return { color: fallback, opacity: 1 };
  }
  const raw = getComputedStyle(document.documentElement as Element)
    .getPropertyValue(name)
    .trim();
  const match = raw.match(/rgba?\(([^)]+)\)/);
  if (match) {
    const [r, g, b, a] = match[1].split(',').map((part) => part.trim());
    return { color: `rgb(${r}, ${g}, ${b})`, opacity: a !== undefined ? Number(a) : 1 };
  }
  return { color: raw || fallback, opacity: 1 };
};

const getWorldPositionWithRotation = (
  originator: any,
  position: any,
  height: number = 0.5
): { position: Vector3 } => {
  const rotationAngle = -originator?.rotation || 0;
  const originatorX = originator?.position?.[0] ?? 0;
  const originatorY = originator?.position?.[1] ?? 0;

  const rotatedX = position[0] * Math.cos(rotationAngle) - position[1] * Math.sin(rotationAngle);
  const rotatedY = position[0] * Math.sin(rotationAngle) + position[1] * Math.cos(rotationAngle);

  return { position: new Vector3(rotatedX + originatorX, height, rotatedY + originatorY) };
};

export interface NumberingLabelsProps {}

export const NumberingLabels: React.FC<NumberingLabelsProps> = memo(() => {
  const graph = useStore(({ graph }) => graph);
  // Re-read theme colors when the theme changes (light/dark).
  const themeMode = useStore(({ theme }) => theme);

  const forceUpdate = useForceUpdate();
  useEffect(() => graph.subscribe(forceUpdate), [forceUpdate, graph]);

  // Mirror the active-tool badge's themed circle (`LabelWrapper`): panel fill,
  // on-surface-variant ring and on-surface text. Recomputed on theme change so
  // it stays readable in light/dark.
  const { panel, border, text } = useMemo(
    () => ({
      panel: readThemeColor('--spz-sys-color-panel', 'rgb(225, 225, 225)'),
      border: readThemeColor('--spz-sys-color-on-surface-variant', 'rgb(73, 69, 79)'),
      text: readThemeColor('--spz-sys-color-on-surface', 'rgb(28, 27, 31)'),
    }),
    // `themeMode` drives recomputation: the tokens are read from the DOM, whose
    // values change with the theme (which ESLint can't see statically).
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [themeMode]
  );

  const nodes: FurnitureNode[] = graph
    .flatten()
    .filter(({ type, visible }: any) => type === FNodeTypesEnum.Furniture && visible) as any;

  return nodes.map((node) => {
    if (!node.selector) {
      return null;
    }
    const shallowNode: FShallowNode | null = graph.shallow.nodes[node.selector] ?? null;
    const { position, originator, selector } = node;
    const positionRotated = getWorldPositionWithRotation(originator, position);
    const numbering = (shallowNode as any)?.meta?.numbering;

    if (!numbering) {
      return null;
    }

    // Render an in-scene, billboarded circular badge (not a DOM overlay) so
    // numbering is included in offscreen captures (screenshots) whether shown
    // via the numbering tool or the capture-panel toggle, while keeping the
    // circular themed look of the original tool badge. `CanvasLabel` auto-sizes
    // from the text bounds, so multi-digit numbers don't overflow.
    return (
      <NumberingLabelWrapper key={selector} selector={originator?.selector ?? selector}>
        <CanvasLabel
          billboard
          bgColor={panel.color}
          bgOpacity={panel.opacity}
          borderColor={border.color}
          borderOpacity={border.opacity}
          borderWidth={0.03}
          color={text.color}
          fontSize={0.22}
          fontWeight={600}
          padding={[0.14, 0.14]}
          position={positionRotated.position ? positionRotated.position : new Vector3(0, 0, 0)}
          shape="circle"
        >
          {String(numbering)}
        </CanvasLabel>
      </NumberingLabelWrapper>
    );
  });
});

export interface NumberingControlsBaseProps {}

export const NumberingControlsBase: React.FC<NumberingControlsBaseProps> = memo(() => {
  const graph = useStore(({ graph }) => graph);
  const { selected } = useSelection();
  const { staging, createAction } = useStaging();

  const forceUpdate = useForceUpdate();
  const nodes: FurnitureNode[] = graph
    .flatten()
    .filter(({ type, visible }: any) => type === FNodeTypesEnum.Furniture && visible) as any;
  const { chairNumberingCounter, tableNumberingCounter } = nodes.reduce(
    (acc, node) => {
      if (!node.selector) {
        return acc;
      }

      const shallowNode = graph.shallow.nodes[node.selector];
      const numbering = (shallowNode as any)?.meta?.numbering;
      if (!numbering) {
        return acc;
      }
      if (/chair/.test(node.furnitureType)) {
        acc.chairNumberingCounter = Math.max(acc.chairNumberingCounter, numbering);
      } else if (/table/.test(node.furnitureType)) {
        acc.tableNumberingCounter = Math.max(acc.tableNumberingCounter, numbering);
      }
      return acc;
    },
    {
      chairNumberingCounter: 0,
      tableNumberingCounter: 0,
    }
  );
  useEffect(() => graph.subscribe(forceUpdate), [forceUpdate, graph]);

  const updateNumbering = useEventCallback((selected: Object3D[]) => {
    let chairCounter = 1;
    let tableCounter = 1;
    let value = 0;

    const numberedNodes = selected.reduce((acc, object) => {
      const selectedNode: FurnitureNode | null = getNodeFromObject(object) as any;
      if (!selectedNode?.selector) {
        return acc;
      }
      const shallowNode: FShallowNode | null = graph.shallow.nodes[selectedNode.selector];
      const numbering = (shallowNode as any)?.meta?.numbering;
      if (
        selectedNode.type === FNodeTypesEnum.Furniture &&
        !numbering &&
        nodes.some((node) => node.selector === selectedNode.selector)
      ) {
        return [...acc, selectedNode];
      }
      return acc;
    }, [] as FurnitureNode[]);
    if (numberedNodes.length) {
      staging.prepare(true);

      numberedNodes.forEach((node) => {
        if (/chair/.test(node.furnitureType)) {
          value = chairNumberingCounter + chairCounter++;
        } else if (/table/.test(node.furnitureType)) {
          value = tableNumberingCounter + tableCounter++;
        }

        staging.add(createAction('update', node.selector!, 'meta.numbering', value));
      });
      staging.commit(CommanderActionTypesEnum.Meta);
    }
  });
  useEffect(() => startTransition(() => updateNumbering(selected)), [selected, updateNumbering]);

  return <NumberingLabels />;
});

export const NumberingControls = memo(() => {
  const tool = useStore(({ tool }) => tool);
  const showNumbering = useStore(({ showNumbering }) => showNumbering);

  if (tool === ToolModeEnum.Numbering) {
    return <NumberingControlsBase />;
  }
  if (showNumbering) {
    return <NumberingLabels />;
  }
  return null;
});
